import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ThemeService } from '@core/services/theme.service';
import { PAGE_TITLES, usePageTitle } from '@core/utils/page-title.util';
import { PagePreviewBaseComponent } from '@feature/preview-management/components/page-preview-base/page-preview-base.component';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, Observable } from 'rxjs';
import { ChooseLanguageComponent } from '../../../../core/components/choose-language/choose-language.component';
import { NavigationComponent } from '../../../../core/components/navigation/navigation.component';
import { AnswerModuleService } from '../../../../core/services/answer-module.service';
import { RedirectService } from '../../../../core/services/redirect.service';
import { LayoutComponent } from '../../../../shared/components/layout/layout.component';
import { AnswerModuleResponse } from '../../../../shared/models/answer-module-response';
import { ChooseLanguage } from '../../../../shared/models/choose-language';
import { LanguageView } from '../../../../shared/models/views-models/language-view';
import {
  createLanguageObject,
  mapLanguageOptions,
  updateTextDirection,
} from '../../../../shared/utils/helper.util';
import {
  cleanupViewportHeight,
  setupViewportHeight,
} from '../../../../shared/utils/viewport.util';

@Component({
  selector: 'app-language-page',
  imports: [
    CommonModule,
    LayoutComponent,
    ChooseLanguageComponent,
    NavigationComponent,
    TranslateModule,
  ],
  templateUrl: './language-page.component.html',
  styleUrl: './language-page.component.scss',
})
export class LanguagePageComponent
  extends PagePreviewBaseComponent
  implements OnInit, OnDestroy
{
  constructor(
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private answerService: AnswerModuleService,
    protected override redirectService: RedirectService,
    protected override themeService: ThemeService,
  ) {
    super(redirectService, themeService);
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event?: PopStateEvent) {
    history.pushState(null, '');
  }

  chooseLanguageView?: LanguageView;
  options: { value: string; label: string; src: string }[] = [];
  selectedLanguage: string = '';

  private readonly destroyRef = inject(DestroyRef);
  private setPageTitle = usePageTitle();
  private beforeUnloadHandler = this.onBeforeUnload.bind(this);

  onBeforeUnload(event: Event): void {
    event.preventDefault();
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    cleanupViewportHeight();
  }
  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    return this.allowNavigation;
  }

  ngOnInit(): void {
    this.setPageTitle(PAGE_TITLES.LANGUAGE.key, PAGE_TITLES.LANGUAGE.fallback);

    // Set viewport height for mobile browsers
    setupViewportHeight();

    if (!this.previewMode) {
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const response = history.state.data as AnswerModuleResponse;
        if (!this.previewMode) {
          this.response = response;
          this.onResponseSet(response);
        }
      });

    if (this.response.language) {
      updateTextDirection(this.response.language);
    }
  }

  protected override onResponseSet(value: AnswerModuleResponse): void {
    if (value?.view?.type === 'CHOOSE_LANGUAGE') {
      this.chooseLanguageView = value.view;
      this.updateOptions();
      this.cdr.detectChanges();
    }
  }
  updateOptions(): void {
    if (this.chooseLanguageView) {
      this.options = mapLanguageOptions(this.chooseLanguageView?.data);
    }
  }

  setLanguage({ language, calledWithInitialLanguage }: ChooseLanguage) {
    this.selectedLanguage = language;

    updateTextDirection(language);

    if (!calledWithInitialLanguage) {
      this.postLanguage();
    }
  }

  postLanguage() {
    if (this.response?.responseId) {
      const languageRequest = createLanguageObject(
        this.response?.responseId,
        this.selectedLanguage,
      );
      this.answerService
        .postLanguage(
          this.response.responseId,
          this.response.stateId,
          languageRequest,
        )
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => {
            this.allowNavigation = true;
            return [];
          }),
        )
        .subscribe();
    }
  }

  next(event?: Event) {
    if (this.response) {
      this.answerService
        .getNextState(this.response?.responseId, this.response?.stateId, [])
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => {
            this.allowNavigation = true;
            return [];
          }),
        )
        .subscribe((res) => {
          if (res.body) {
            this.handlePreviewMode(res.body);
          }
        });
    }
  }
}
